import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { Newspaper } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsPage() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => {
    api.get("/news").then((res) => setItems(res.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    load();
  }, []);

  const openItem = (n) => {
    setActive(n);
    if (!n.read) {
      api.post(`/news/${n.id}/read`).then(load).catch(() => {});
    }
  };

  useEffect(() => {
    const openId = params.get("open");
    if (openId && Array.isArray(items)) {
      const target = items.find((n) => n.id === openId);
      if (target) {
        openItem(target);
        setParams({}, { replace: true });
      }
    }
  }, [items, params]);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="news-page">
      <div>
        <p className="eyebrow-label">PlusMaker Updates</p>
        <h1 className="page-title mt-2">News &amp; Announcements</h1>
        <p className="page-sub mt-1">Official announcements from the PlusMaker ecosystem.</p>
      </div>

      {items === null ? (
        <div className="grid sm:grid-cols-2 gap-5" data-testid="news-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card-lux overflow-hidden">
              <Skeleton className="h-44 w-full rounded-none" />
              <div className="p-5 space-y-2.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card-lux p-10 text-center" data-testid="news-empty">
          <Newspaper className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" />
          <p className="text-[#64748B] text-sm">No announcements yet. Check back soon.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((n) => (
            <article key={n.id} className="card-lux overflow-hidden group" data-testid={`news-card-${n.id.slice(-6)}`}>
              {n.image_url && (
                <div className="h-44 overflow-hidden">
                  <img loading="lazy" src={`${API}${n.image_url}`} alt={n.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5 space-y-2.5">
                <div className="flex items-center gap-2.5">
                  {!n.read && <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" data-testid={`news-unread-dot-${n.id.slice(-6)}`} />}
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">
                    {n.published_at ? fmtMYT(n.published_at) : ""}
                  </p>
                </div>
                <h3 className="text-white font-semibold leading-snug">{n.title}</h3>
                <p className="text-xs text-[#94A3B8] leading-relaxed line-clamp-3">{n.content}</p>
                <button onClick={() => openItem(n)} className="text-xs text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-4 transition-colors" data-testid={`news-read-more-${n.id.slice(-6)}`}>
                  Read More
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setActive(null)} />
          <div className="relative card-lux w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="news-modal">
            {active.image_url && (
              <img loading="lazy" src={`${API}${active.image_url}`} alt={active.title} className="w-full max-h-72 object-cover" data-testid="news-modal-image" />
            )}
            <div className="p-6 sm:p-8 space-y-4">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">
                {active.published_at ? fmtMYT(active.published_at) : ""}
              </p>
              <h2 className="text-xl sm:text-2xl font-semibold text-white" data-testid="news-modal-title">{active.title}</h2>
              <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-line" data-testid="news-modal-content">{active.content}</p>
              <div className="pt-2">
                <button onClick={() => setActive(null)} className="btn-gold-outline px-6 py-2.5 text-xs" data-testid="news-modal-close">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
